import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import Reveal, { StaggerReveal } from "../components/motion/Reveal";
import {
  ArrowRight, Check, Sparkles, FileSpreadsheet, CalendarX2, FileText, CreditCard,
  Copy, ZapOff, Monitor, Brain, Car, ClipboardList, Users, Camera, Receipt, Globe,
  UserCog, CalendarDays, MailCheck, Map, Compass, MessagesSquare, Building2,
  TrendingUp, Cpu, Quote,
} from "lucide-react";

const PAINS = [
  { icon: FileSpreadsheet, title: "Excel comme colonne vertébrale", desc: "Le planning, les tarifs et les clients vivent dans des tableurs fragiles, recopiés à la main." },
  { icon: CalendarX2, title: "Un agenda séparé", desc: "Les réservations sont notées ailleurs que là où vivent les véhicules. Les conflits passent inaperçus." },
  { icon: FileText, title: "Des contrats Word", desc: "Chaque départ commence par un copier-coller. Chaque contrat est légèrement différent du précédent." },
  { icon: CreditCard, title: "Des paiements externes", desc: "Virements promis, espèces, TPE isolé : rien n'est relié à la réservation qu'il concerne." },
  { icon: Copy, title: "Des données dupliquées", desc: "Le même client existe trois fois, dans trois outils, avec trois orthographes différentes." },
  { icon: ZapOff, title: "Aucune automatisation", desc: "Emails, relances, tâches, factures : tout repose sur la mémoire et la discipline de chacun." },
  { icon: Monitor, title: "Des interfaces vieillissantes", desc: "Des logiciels pensés il y a quinze ans, à quinze menus, que personne n'aime ouvrir." },
  { icon: Brain, title: "Zéro intelligence métier", desc: "Aucun outil ne comprend l'activité : personne ne vous dit ce qui marche, ni ce qui dort." },
];

const DOMAINS = [
  { icon: Car, label: "Flotte" },
  { icon: ClipboardList, label: "Réservations" },
  { icon: Users, label: "Clients" },
  { icon: FileText, label: "Contrats" },
  { icon: Camera, label: "États des lieux" },
  { icon: CreditCard, label: "Paiements" },
  { icon: Receipt, label: "Revenus" },
  { icon: Globe, label: "Site internet de réservation" },
  { icon: UserCog, label: "Équipe" },
  { icon: Sparkles, label: "IA" },
];

const COPILOT_POINTS = [
  "Il comprend vos données : véhicules, réservations, revenus, clients.",
  "Il analyse votre activité et repère ce qui marche — et ce qui dort.",
  "Il prépare les actions : tâches, relances, rapports.",
  "Il propose les prochaines étapes, avec une explication claire.",
  "Il vous aide à décider, chiffres à l'appui.",
  "Et il vous laisse toujours le contrôle : vous validez, il exécute.",
];

const CHAIN = [
  { icon: CalendarDays, label: "Calendrier", to: "/fonctionnalites/pilotage-calendrier" },
  { icon: ClipboardList, label: "Réservation", to: "/fonctionnalites/reservations-devis" },
  { icon: FileText, label: "Contrat", to: "/fonctionnalites/contrats-signature" },
  { icon: CreditCard, label: "Paiement", to: "/fonctionnalites/encaissements" },
  { icon: MailCheck, label: "Email", to: "/fonctionnalites/emails-automatiques" },
  { icon: Globe, label: "Site internet", to: "/fonctionnalites/page-de-reservation" },
  { icon: Receipt, label: "Revenus", to: "/fonctionnalites/revenus-facturation" },
  { icon: Sparkles, label: "Copilot", to: "/fonctionnalites/assistant-ia" },
  { icon: UserCog, label: "Équipe", to: "/fonctionnalites/equipe-securite" },
];

const TERRAIN = [
  { icon: MessagesSquare, title: "Les retours terrain", desc: "Chaque suggestion d'un loueur est lue, priorisée et souvent livrée en quelques semaines." },
  { icon: Building2, title: "Les besoins réels des agences", desc: "Multi-agences, rôles, périmètres : la plateforme épouse les organisations réelles, pas l'inverse." },
  { icon: TrendingUp, title: "Les évolutions du secteur", desc: "Réservation en ligne, paiement instantané, attentes clients : Junago avance au rythme du marché." },
  { icon: Cpu, title: "Les nouvelles capacités de l'IA", desc: "Chaque progrès de l'IA est évalué avec une seule question : est-ce réellement utile à un loueur ?" },
];

function ChapterHead({ n, overline, title, sub, center }) {
  return (
    <div className={center ? "text-center" : ""}>
      <Reveal>
        <div className="text-xs font-bold tracking-[0.25em] uppercase text-violet-300">Chapitre {n} — {overline}</div>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-4 font-heading text-2xl md:text-4xl font-medium tracking-tight text-white leading-tight">{title}</h2>
      </Reveal>
      {sub && (
        <Reveal delay={150}>
          <p className={`mt-4 text-sm md:text-base text-slate-400 leading-relaxed ${center ? "max-w-2xl mx-auto" : "max-w-2xl"}`}>{sub}</p>
        </Reveal>
      )}
    </div>
  );
}

export default function PourquoiJunago() {
  useEffect(() => {
    document.title = "Pourquoi Junago ? Notre vision pour les professionnels de la location";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#07070F] text-white">
      <PublicNavbar />

      {/* ─── Hero manifeste ─── */}
      <section className="relative -mt-16 pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden" data-testid="why-hero">
        <div aria-hidden className="absolute top-[-180px] left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-violet-600/20 blur-[130px] pointer-events-none" />
        <div aria-hidden className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)", backgroundSize: "90px 90px" }} />
        <div aria-hidden className="absolute top-40 left-[16%] h-2 w-2 rounded-full bg-violet-400/60 hidden md:block" style={{ animation: "orbitFloat 9s ease-in-out infinite" }} />
        <div aria-hidden className="absolute top-64 right-[14%] h-2.5 w-2.5 rounded-full bg-fuchsia-400/50 hidden md:block" style={{ animation: "orbitFloat 7s 1.2s ease-in-out infinite" }} />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-bold tracking-[0.2em] uppercase text-violet-300">
              <Compass className="h-3.5 w-3.5" /> Entreprise · Manifeste
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-7 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08]" data-testid="why-title">
              Pourquoi <span className="text-gradient-animated">Junago</span> ?
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-7 text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Parce que les professionnels de la location méritent mieux qu'un empilement
              de logiciels, de tableaux et de tâches manuelles.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Chapitre 1 — Le constat ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="why-chapter-1">
        <ChapterHead
          n={1}
          overline="Le constat"
          title="Les outils de location sont encore trop fragmentés."
          sub="Dans la plupart des agences, la journée d'un loueur ressemble encore à ceci :"
        />
        <StaggerReveal className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" step={80} y={24}>
          {PAINS.map((p) => (
            <div key={p.title} className="group rounded-2xl bg-white/[0.03] border border-white/10 p-6 transition-all duration-300 hover:bg-white/[0.06] hover:border-red-400/30 hover:-translate-y-1">
              <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-white/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <p.icon className="h-5 w-5 text-red-300" />
              </div>
              <div className="mt-4 font-heading text-[15px] font-semibold text-white leading-snug">{p.title}</div>
              <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </StaggerReveal>
      </section>

      {/* ─── Chapitre 2 — Une seule plateforme ─── */}
      <section className="relative overflow-hidden border-y border-white/5 bg-white/[0.02]" data-testid="why-chapter-2">
        <div aria-hidden className="absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-violet-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24 text-center">
          <ChapterHead
            n={2}
            overline="Une autre manière de travailler"
            title="Une seule plateforme. Une seule source de vérité."
            sub="Chez Junago, tout ce qui fait tourner une activité de location vit au même endroit — et chaque information n'est saisie qu'une seule fois."
            center
          />
          <StaggerReveal className="mt-12 flex flex-wrap justify-center gap-3" step={60} y={16}>
            {DOMAINS.map((d) => (
              <span key={d.label} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-violet-400/40 hover:bg-violet-500/10 hover:-translate-y-0.5">
                <d.icon className="h-4 w-4 text-violet-300" /> {d.label}
              </span>
            ))}
          </StaggerReveal>
          <Reveal delay={200}>
            <p className="mt-10 text-sm text-slate-400 max-w-xl mx-auto">
              Fini les doubles saisies, les versions contradictoires et les « je vérifie et je te rappelle » :
              la donnée est unique, vivante, et toute l'équipe regarde la même.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Chapitre 3 — L'IA doit travailler réellement ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="why-chapter-3">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <ChapterHead
              n={3}
              overline="L'IA doit travailler réellement"
              title="L'IA n'est pas un gadget."
              sub="Junago Copilot n'est pas un chatbot posé sur un logiciel. C'est une intelligence native, branchée sur vos vraies données :"
            />
            <div className="mt-8 space-y-3">
              {COPILOT_POINTS.map((p, i) => (
                <Reveal key={p} delay={100 + i * 90}>
                  <div className="flex items-start gap-3">
                    <span className="h-5 w-5 rounded-full bg-violet-500/15 border border-violet-400/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-violet-300" strokeWidth={3} />
                    </span>
                    <p className="text-sm text-slate-300 leading-relaxed">{p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={150} y={36}>
            <div className="relative">
              <div aria-hidden className="absolute -inset-6 opacity-50 blur-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse at 40% 30%, rgba(124,58,237,0.35), transparent 60%)" }} />
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/15 shadow-[0_50px_110px_-40px_rgba(124,58,237,0.5)]">
                <img src="/captures/copilot.jpg" alt="Junago Copilot — capture réelle du logiciel" className="w-full block" loading="lazy" />
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center">Capture réelle de Junago Copilot.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Chapitre 4 — Tout est connecté ─── */}
      <section className="relative overflow-hidden border-y border-white/5 bg-white/[0.02]" data-testid="why-chapter-4">
        <div aria-hidden className="absolute -bottom-40 right-1/4 h-80 w-80 rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <ChapterHead
            n={4}
            overline="Tout est connecté"
            title="Une plateforme unifiée, pas une collection de modules."
            sub="Une réservation arrive : le calendrier se bloque, le contrat se génère, le paiement se déclenche, l'email part, le site se met à jour, les revenus se calculent, le Copilot prépare l'équipe. Une seule action, toute la plateforme en mouvement."
            center
          />
          <div className="mt-12 flex flex-wrap justify-center items-center gap-y-5">
            {CHAIN.map((n, i) => (
              <React.Fragment key={n.label}>
                <Reveal delay={100 + i * 80} as="span" className="inline-block">
                  <Link
                    to={n.to}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 text-sm font-semibold text-slate-200 transition-all duration-300 hover:-translate-y-0.5 hover:border-violet-400/50 hover:bg-violet-500/10 hover:text-white"
                    data-testid={`why-chain-${i}`}
                  >
                    <n.icon className="h-4 w-4 text-violet-300" /> {n.label}
                  </Link>
                </Reveal>
                {i < CHAIN.length - 1 && (
                  <span aria-hidden className="mx-1.5 flow-dash hidden sm:block" style={{ animationDelay: `${i * 0.25}s` }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Chapitre 5 — Construite avec le terrain ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="why-chapter-5">
        <ChapterHead
          n={5}
          overline="Une plateforme construite avec le terrain"
          title="Junago évolue avec les loueurs."
          sub="Le produit n'est pas figé : il avance en permanence, guidé par quatre moteurs."
        />
        <StaggerReveal className="mt-12 grid sm:grid-cols-2 gap-5" step={100} y={26}>
          {TERRAIN.map((t) => (
            <div key={t.title} className="group rounded-2xl bg-white/[0.03] border border-white/10 p-7 transition-all duration-300 hover:bg-white/[0.06] hover:border-violet-400/40 hover:-translate-y-1">
              <div className="h-11 w-11 rounded-xl bg-violet-500/10 border border-white/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-6deg]">
                <t.icon className="h-5 w-5 text-violet-300" />
              </div>
              <div className="mt-4 font-heading text-lg font-medium text-white">{t.title}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </StaggerReveal>
        <Reveal delay={200}>
          <div className="mt-9">
            <Link to="/assistance/roadmap" className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-white transition-colors" data-testid="why-roadmap-link">
              <Map className="h-4 w-4" /> Découvrir la Roadmap <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ─── Conclusion ─── */}
      <section className="relative overflow-hidden" data-testid="why-conclusion">
        <div aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[380px] w-[680px] rounded-full bg-violet-600/15 blur-[120px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 py-20 md:py-28 text-center">
          <Reveal><Quote className="h-8 w-8 text-violet-400 mx-auto" /></Reveal>
          <Reveal delay={120}>
            <p className="mt-6 font-heading text-2xl md:text-4xl font-medium tracking-tight leading-snug">
              Nous ne construisons pas seulement un logiciel.
              <br />
              <span className="text-gradient-animated">Nous construisons la prochaine génération d'outils</span>
              <br />
              pour les professionnels de la location.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── CTA final ─── */}
      <section className="max-w-5xl mx-auto px-6 pb-20 md:pb-28" data-testid="why-cta">
        <Reveal y={44}>
          <div className="sheen-card rounded-3xl border border-white/10 bg-gradient-to-br from-violet-950/60 to-[#0B0B16] p-10 md:p-14 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -top-24 right-1/4 h-64 w-64 rounded-full bg-violet-600/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-heading text-2xl md:text-4xl font-medium tracking-tight text-white">
                Jugez sur pièces.
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-400 max-w-lg mx-auto">
                30 jours d'essai complet, sans carte bancaire — la meilleure façon de comprendre Junago, c'est de l'ouvrir.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register?context=vendor" className="btn-jn-light inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold" data-testid="why-cta-start">
                  Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
                </Link>
                <Link to="/fonctionnalites" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="why-cta-features">
                  Découvrir les fonctionnalités
                </Link>
              </div>
              <Link to="/assistance/roadmap" className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors" data-testid="why-cta-roadmap">
                Voir la Roadmap <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
